import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../../services/UserService';
import { tenantQueryKeys } from '../tenantQueryKeys';

function invalidateLabelAssignments(
  queryClient: ReturnType<typeof useQueryClient>,
  labelId?: string,
  userId?: string,
) {
  void queryClient.invalidateQueries({ queryKey: tenantQueryKeys.labels() });
  if (labelId) {
    void queryClient.invalidateQueries({
      queryKey: tenantQueryKeys.labelUsers(labelId),
    });
    void queryClient.invalidateQueries({
      queryKey: tenantQueryKeys.label(labelId),
    });
  }
  if (userId) {
    void queryClient.invalidateQueries({
      queryKey: tenantQueryKeys.userLabels(userId),
    });
  } else {
    void queryClient.invalidateQueries({
      queryKey: [...tenantQueryKeys.all, 'userLabels'],
    });
  }
}

export function useCreateTenantLabelMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => userService.createLabel(name),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.labels(),
      });
    },
  });
}

export function useRenameTenantLabelMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ labelId, name }: { labelId: string; name: string }) =>
      userService.renameLabel(labelId, name),
    onSuccess: (_result, variables) => {
      invalidateLabelAssignments(queryClient, variables.labelId);
    },
  });
}

export function useDeleteTenantLabelMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (labelId: string) => userService.deleteLabel(labelId),
    onSuccess: (_result, labelId) => {
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.labels(),
      });
      void queryClient.removeQueries({
        queryKey: tenantQueryKeys.labelUsers(labelId),
      });
      void queryClient.removeQueries({
        queryKey: tenantQueryKeys.label(labelId),
      });
      void queryClient.invalidateQueries({
        queryKey: [...tenantQueryKeys.all, 'userLabels'],
      });
    },
  });
}

export function useAssignTenantUserLabelMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, labelId }: { userId: string; labelId: string }) =>
      userService.assignUserLabel(userId, labelId),
    onSuccess: (_result, variables) => {
      invalidateLabelAssignments(
        queryClient,
        variables.labelId,
        variables.userId,
      );
    },
  });
}

export function useUnassignTenantUserLabelMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, labelId }: { userId: string; labelId: string }) =>
      userService.unassignUserLabel(userId, labelId),
    onSuccess: (_result, variables) => {
      invalidateLabelAssignments(
        queryClient,
        variables.labelId,
        variables.userId,
      );
    },
  });
}
